import { useState, useEffect, useMemo, useCallback } from "react";
import { memberLevelStore } from "../utils/memberLevelStore";
import { contributionStore } from "../utils/contributionStore";
import { activityStore, voteStore } from "../utils/dataStore";
import type { LevelChangeLog } from "./MemberList";
import { fetchMemberLevelChangeLogs } from "../../../api/memberLevelChangeLogs";
import { fetchPointLedger, type PointLedgerRow } from "../../../api/pointLedger";
import { fetchTenantProfile, type TenantProfileDto } from "../../../api/tenantProfile";
import { reportError, showUserNotice } from "../../../api/errors";
import {
  fetchEnrollmentApplication,
  fetchMemberActivityParticipation,
  fetchMemberFeeRecords,
  postMemberManualPoints,
  type ActivityParticipationRow,
  type EnrollmentApplicationDto,
  type MemberFeeRecordRow,
} from "../../../api/memberAdminExtras";
import {
  changeMemberNo,
  fetchMemberNoRule,
  fetchReservedMemberNos,
} from "../../../api/memberNoRule";
import { composeMemberNoFromRule } from "../utils/memberNoCompose";
import { fetchAgreementDocuments } from "../../../api/agreementDocuments";
import AgreementEnrollmentPreviewModal, {
  pickEnrollmentAgreementDoc,
} from "./AgreementEnrollmentPreviewModal";
import {
  mergeSavedAgreementDocuments,
  type AgreementDocumentModel,
} from "../utils/defaultAgreementDocuments";
import {
  enabledMemberGroupDefinitions,
  enabledRolesForGroup,
  fetchAllMemberGroupDefinitionsForTenant,
  formatMemberGroupsDisplay,
  memberGroupsFromMemberRow,
  type MemberGroupDefinition,
} from "../../../api/memberGroupDefinitions";
import { putMemberMemberGroups } from "../../../api/memberMemberGroup";

function mapDtoToLevelLog(r: {
  id: string;
  oldLevel: string;
  newLevel: string;
  note: string | null;
  operator: string;
  createdAt: string;
}): LevelChangeLog {
  const d = new Date(r.createdAt);
  return {
    id: r.id,
    date: d.toISOString().slice(0, 10),
    time: d.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" }),
    oldLevel: r.oldLevel,
    newLevel: r.newLevel,
    operator: r.operator,
    note: r.note ?? undefined,
  };
}

type Member = {
  id: string; name: string; realName: string; email: string;
  phone: string; city: string; level: string; status: string;
  joinDate: string; points: number; memberNo?: string;
  wxOpenId?: string | null;
  membershipValidUntil?: string | null;
  memberGroupKey?: string | null;
  memberGroupName?: string | null;
  memberGroupRoleKey?: string | null;
  memberGroupRoleName?: string | null;
  memberGroups?: Array<{
    memberGroupKey: string;
    memberGroupName?: string | null;
    memberGroupRoleKey?: string | null;
    memberGroupRoleName?: string | null;
  }> | null;
};

type Tab = "info" | "agreement" | "fee" | "activity" | "vote" | "points" | "contribution" | "history";

interface Props {
  tenantLegacyId: string;
  member: Member;
  onClose: () => void;
  onDataChanged?: () => void;
}

const seed = (s: string) => s.split("").reduce((h, c) => ((h * 31 + c.charCodeAt(0)) & 0x7fffffff), 0);

function categoryLabelZh(cat: string): string {
  switch (cat) {
    case "signin":
      return "签到";
    case "activity":
      return "活动";
    case "exchange":
      return "兑换";
    case "vote":
      return "投票";
    case "contribution":
      return "贡献";
    default:
      return cat || "其他";
  }
}

function activityParticipationUi(a: ActivityParticipationRow): { status: string; rowClass: string } {
  if (a.checkedIn)
    return { status: "已参与", rowClass: "bg-green-50 text-green-600" };
  if (a.registrationStatus === "rejected")
    return { status: "已驳回", rowClass: "bg-gray-100 text-gray-500" };
  if (a.activityStatus === "ended")
    return { status: "已结束", rowClass: "bg-gray-100 text-gray-500" };
  return { status: "已报名", rowClass: "bg-amber-50 text-amber-600" };
}

const typeColor: Record<string, string> = {
  "观赛活动": "bg-red-50 text-red-600", "线下聚会": "bg-amber-50 text-amber-600",
  "志愿活动": "bg-pink-50 text-pink-600", "年度大会": "bg-teal-50 text-teal-600",
};

interface AddContribForm {
  categoryId: string; quantity: number | ""; date: string; activityId: string;
  customEvent: string; role: string;
}
const emptyAddForm = (): AddContribForm => ({
  categoryId: "", quantity: "", date: new Date().toISOString().slice(0, 10),
  activityId: "", customEvent: "", role: "",
});

function VoucherModal({
  record,
  payeeName,
  collectModeOnline,
  onClose,
}: {
  record: MemberFeeRecordRow;
  payeeName: string;
  collectModeOnline: boolean;
  onClose: () => void;
}) {
  const proofUrl = record.paymentProofUrl?.trim() ?? "";

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[min(90vh,640px)] overflow-hidden flex flex-col">
        <div className="bg-green-600 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <i className="ri-receipt-line text-white text-lg"></i>
            <h3 className="font-bold text-white text-sm">缴费凭证</h3>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-green-500 cursor-pointer text-white">
            <i className="ri-close-line"></i>
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          {record.amount > 0 ? (
            <>
              <div className="bg-green-50 border border-green-100 rounded-2xl p-4 text-center">
                <p className="text-xs text-green-600 mb-1">实收金额（元）</p>
                <p className="text-3xl font-bold text-green-700">¥{record.amount}</p>
                <p className="text-xs text-green-600 mt-1">{record.method}</p>
              </div>
              {collectModeOnline ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-center">
                  <p className="text-sm font-semibold text-emerald-800">已在线微信支付 ¥{record.amount}</p>
                </div>
              ) : proofUrl ? (
                <div>
                  <p className="text-[11px] font-medium text-gray-500 mb-2">上传的缴费凭证</p>
                  <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                    <img
                      referrerPolicy="no-referrer"
                      src={proofUrl}
                      alt="缴费凭证"
                      className="w-full max-h-72 object-contain object-top bg-gray-100"
                    />
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-center">
                  <p className="text-xs text-gray-500">本条记录未关联凭证图片（历史数据或未上传）</p>
                </div>
              )}
              <div className="space-y-2 text-sm">
                {[
                  { label: "缴费项目", value: record.type },
                  { label: "缴费日期", value: record.date },
                  { label: "收款方", value: payeeName || "协会" },
                  { label: "记录编号", value: record.id },
                  { label: "缴费状态", value: record.status },
                ].map((row) => (
                  <div key={row.label} className="flex justify-between items-center py-2 border-b border-gray-50 gap-3">
                    <span className="text-gray-400 text-xs flex-shrink-0">{row.label}</span>
                    <span className="text-gray-800 font-medium text-xs text-right break-all">{row.value}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2.5">
                <i className="ri-information-line text-slate-400 flex-shrink-0"></i>
                <p className="text-xs text-slate-600 leading-relaxed">凭证与金额来自入会申请或续费申请存档，以服务端数据为准。</p>
              </div>
            </>
          ) : (
            <div className="text-center py-6">
              <i className="ri-gift-line text-4xl text-green-400 block mb-3"></i>
              <p className="font-medium text-gray-700">本次为免费注册</p>
              <p className="text-xs text-gray-400 mt-1">普通会员注册无需缴费</p>
              {proofUrl ? (
                <div className="mt-4 text-left">
                  <p className="text-[11px] font-medium text-gray-500 mb-2">存档凭证图</p>
                  <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                    <img
                      referrerPolicy="no-referrer"
                      src={proofUrl}
                      alt="缴费凭证"
                      className="w-full max-h-56 object-contain object-top bg-gray-100"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MemberDetailModal({ tenantLegacyId, member, onClose, onDataChanged }: Props) {
  const [tab, setTab] = useState<Tab>("info");
  const [levelLogs, setLevelLogs] = useState<LevelChangeLog[]>([]);
  const [levelLogsLoading, setLevelLogsLoading] = useState(false);

  const [profile, setProfile] = useState<TenantProfileDto | null>(null);
  const [enrollment, setEnrollment] = useState<EnrollmentApplicationDto | null>(null);
  const [enrollmentLoading, setEnrollmentLoading] = useState(true);
  const [activityRows, setActivityRows] = useState<ActivityParticipationRow[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [feeRecords, setFeeRecords] = useState<MemberFeeRecordRow[]>([]);
  const [feeLoading, setFeeLoading] = useState(true);
  const [pointRows, setPointRows] = useState<PointLedgerRow[]>([]);
  const [pointsLoading, setPointsLoading] = useState(true);
  const [displayPoints, setDisplayPoints] = useState(member.points);
  const [contribSaving, setContribSaving] = useState(false);
  const [memberNoLocal, setMemberNoLocal] = useState(member.memberNo ?? "");
  const [memberNoEditing, setMemberNoEditing] = useState(false);
  const [memberNoDraft, setMemberNoDraft] = useState("");
  const [memberNoBusy, setMemberNoBusy] = useState(false);
  const [reservedNoOptions, setReservedNoOptions] = useState<string[]>([]);

  const [groupDefs, setGroupDefs] = useState<MemberGroupDefinition[]>([]);
  const [groupDefsLoading, setGroupDefsLoading] = useState(false);
  const [groupDraft, setGroupDraft] = useState<Array<{ groupKey: string; roleKey: string }>>([]);
  const [groupSaving, setGroupSaving] = useState(false);
  const [groupsLocal, setGroupsLocal] = useState(() => memberGroupsFromMemberRow(member));

  useEffect(() => {
    setMemberNoLocal(member.memberNo ?? "");
  }, [member.id, member.memberNo]);

  useEffect(() => {
    const current = memberGroupsFromMemberRow(member);
    setGroupsLocal(current);
    setGroupDraft(
      current.map((g) => ({
        groupKey: String(g.memberGroupKey || "").trim(),
        roleKey: String(g.memberGroupRoleKey || "").trim(),
      })),
    );
  }, [member]);

  useEffect(() => {
    let cancelled = false;
    setGroupDefsLoading(true);
    void fetchAllMemberGroupDefinitionsForTenant(tenantLegacyId)
      .then((defs) => {
        if (!cancelled) setGroupDefs(enabledMemberGroupDefinitions(defs));
      })
      .catch(() => {
        if (!cancelled) setGroupDefs([]);
      })
      .finally(() => {
        if (!cancelled) setGroupDefsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  const toggleGroupKey = (groupKey: string) => {
    setGroupDraft((prev) => {
      const exists = prev.some((x) => x.groupKey === groupKey);
      if (exists) return prev.filter((x) => x.groupKey !== groupKey);
      const def = groupDefs.find((g) => g.id === groupKey);
      const roles = def ? enabledRolesForGroup(def) : [];
      return [...prev, { groupKey, roleKey: roles[0]?.roleKey ?? "" }];
    });
  };

  const setGroupRole = (groupKey: string, roleKey: string) => {
    setGroupDraft((prev) => prev.map((x) => (x.groupKey === groupKey ? { ...x, roleKey } : x)));
  };

  const saveMemberGroups = async () => {
    if (groupSaving) return;
    setGroupSaving(true);
    try {
      const r = await putMemberMemberGroups(
        tenantLegacyId,
        member.id,
        groupDraft
          .filter((x) => x.groupKey)
          .map((x) => ({
            memberGroupKey: x.groupKey,
            memberGroupRoleKey: x.roleKey || null,
          })),
      );
      const saved = Array.isArray(r.memberGroups) ? r.memberGroups : [];
      setGroupsLocal(saved);
      setGroupDraft(
        saved.map((g) => ({
          groupKey: String(g.memberGroupKey || "").trim(),
          roleKey: String(g.memberGroupRoleKey || "").trim(),
        })),
      );
      showUserNotice(saved.length > 0 ? `已设置 ${saved.length} 个分组` : "已清除分组");
      onDataChanged?.();
    } catch (e) {
      reportError(e, "保存分组失败");
    } finally {
      setGroupSaving(false);
    }
  };

  const reloadMemberSnapshot = useCallback(async () => {
    setEnrollmentLoading(true);
    setActivityLoading(true);
    setFeeLoading(true);
    setPointsLoading(true);
    try {
      const [prof, enr, acts, fees, ledger] = await Promise.all([
        fetchTenantProfile(tenantLegacyId).catch(() => null),
        fetchEnrollmentApplication(tenantLegacyId, member.id).catch(
          () => ({ found: false }) as EnrollmentApplicationDto
        ),
        fetchMemberActivityParticipation(tenantLegacyId, member.id).catch(() => []),
        fetchMemberFeeRecords(tenantLegacyId, member.id).catch(() => []),
        fetchPointLedger(tenantLegacyId, member.id, 120).catch(() => []),
      ]);
      if (prof) setProfile(prof);
      setEnrollment(enr);
      setActivityRows(acts);
      setFeeRecords(fees);
      setPointRows(ledger);
      if (ledger.length > 0) setDisplayPoints(ledger[0].balanceAfter);
      else setDisplayPoints(member.points);
    } finally {
      setEnrollmentLoading(false);
      setActivityLoading(false);
      setFeeLoading(false);
      setPointsLoading(false);
    }
  }, [tenantLegacyId, member.id, member.points]);

  useEffect(() => {
    setDisplayPoints(member.points);
    void reloadMemberSnapshot();
  }, [member.points, member.id, reloadMemberSnapshot]);

  useEffect(() => {
    let cancelled = false;
    setLevelLogsLoading(true);
    void fetchMemberLevelChangeLogs(tenantLegacyId, member.id)
      .then((rows) => {
        if (!cancelled) setLevelLogs(rows.map(mapDtoToLevelLog));
      })
      .catch(() => {
        if (!cancelled) setLevelLogs([]);
      })
      .finally(() => {
        if (!cancelled) setLevelLogsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId, member.id]);

  const [contribCatRev, setContribCatRev] = useState(0);
  useEffect(() => contributionStore.subscribe(() => setContribCatRev((n) => n + 1)), []);

  const [showAddContrib, setShowAddContrib] = useState(false);
  const [addForm, setAddForm] = useState<AddContribForm>(emptyAddForm());
  const [showAgreementPreview, setShowAgreementPreview] = useState(false);
  const [voucherRecord, setVoucherRecord] = useState<MemberFeeRecordRow | null>(null);
  /** 后台「协议与文书」中用于入会的文书（与预览弹窗同源） */
  const [agreementDocLoading, setAgreementDocLoading] = useState(true);
  const [enrollmentAgreementDoc, setEnrollmentAgreementDoc] = useState<AgreementDocumentModel | null>(null);

  useEffect(() => {
    let cancelled = false;
    setAgreementDocLoading(true);
    void fetchAgreementDocuments(tenantLegacyId)
      .then((rows) => {
        if (cancelled) return;
        setEnrollmentAgreementDoc(pickEnrollmentAgreementDoc(mergeSavedAgreementDocuments(rows)));
      })
      .catch(() => {
        if (cancelled) return;
        setEnrollmentAgreementDoc(pickEnrollmentAgreementDoc(mergeSavedAgreementDocuments([])));
      })
      .finally(() => {
        if (!cancelled) setAgreementDocLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  const levelCls = memberLevelStore.getClassName(member.level);
  const h = seed(member.id);

  const contributionLedger = useMemo(
    () => pointRows.filter((r) => r.category === "contribution"),
    [pointRows]
  );
  const totalContribCredits = contributionLedger.reduce((s, c) => s + Math.max(0, c.delta), 0);
  const voteRecords = useMemo(() => {
    const out: { title: string; voted: string; time: string; status: string }[] = [];
    for (const v of voteStore.getAll()) {
      const p = v.participants?.find((x) => x.memberId === member.id);
      if (!p) continue;
      out.push({
        title: v.title,
        voted: p.votedOptions.join("、"),
        time: p.time,
        status: v.status === "active" ? "进行中" : v.status === "ended" ? "已结束" : "草稿",
      });
    }
    return out;
  }, [member.id]);

  const activeCategories = useMemo(() => contributionStore.getActive(), [contribCatRev]);
  const activities = activityStore.getAll();
  const selectedCat = activeCategories.find((c) => c.id === addForm.categoryId);
  const autoPoints = selectedCat && addForm.quantity !== "" ? selectedCat.pointsPerUnit * Number(addForm.quantity) : 0;
  const canSubmitContrib = addForm.categoryId && addForm.quantity !== "" && Number(addForm.quantity) > 0 && addForm.date;

  const assocDisplayName = profile?.name?.trim() || "协会";
  const assocShortName = profile?.shortName?.trim() || assocDisplayName;
  const agreementOrgLabel = assocShortName.trim() || assocDisplayName.trim() || "本协会";
  const collectModeOnline = (profile?.enrollmentFeeCollectMode ?? 0) === 1;

  const enrollmentSnapshotDoc = useMemo((): AgreementDocumentModel | null => {
    if (!enrollment || !enrollment.found) return null;
    const c = enrollment.agreementSnapshotContent?.trim();
    if (!c) return null;
    return {
      id: "enrollment-snapshot",
      title: enrollment.agreementSnapshotTitle?.trim() || `${agreementOrgLabel}入会协议`,
      type: "disclaimer",
      content: c,
      requireSign: true,
      version: enrollment.agreementSnapshotVersion?.trim() || "申请存档",
      updatedAt: enrollment.applyDate,
      isActive: true,
    };
  }, [enrollment, agreementOrgLabel]);

  const agreementCardDoc = enrollmentSnapshotDoc ?? enrollmentAgreementDoc;
  const agreementTabBusy = enrollmentLoading || (!enrollmentSnapshotDoc && agreementDocLoading);

  const sealSource = (assocShortName.trim() || assocDisplayName.trim() || agreementOrgLabel).trim();
  const sealMid = Math.max(1, Math.ceil(sealSource.length / 2));
  const sealLine1 = sealSource.slice(0, sealMid) || agreementOrgLabel.slice(0, 6);
  const sealLine2 = sealSource.length > sealMid ? sealSource.slice(sealMid) : "协会";

  const handleAddContrib = async () => {
    if (!canSubmitContrib || !selectedCat || contribSaving) return;
    const actObj = activities.find((a) => a.id === addForm.activityId);
    const eventName = actObj ? actObj.title : (addForm.customEvent.trim() || "—");
    const rolePart = addForm.role.trim() ? ` · ${addForm.role.trim()}` : "";
    const memo = `${selectedCat.name} · ${eventName} · ${addForm.quantity}${selectedCat.unit}${rolePart}`;
    setContribSaving(true);
    try {
      const activityLegacyId = addForm.activityId.trim() || undefined;
      const { points } = await postMemberManualPoints(tenantLegacyId, member.id, {
        delta: autoPoints,
        category: "contribution",
        memo,
        activityLegacyId,
      });
      setDisplayPoints(points);
      await reloadMemberSnapshot();
      onDataChanged?.();
      setShowAddContrib(false);
      setAddForm(emptyAddForm());
    } catch (e) {
      reportError(e, "保存失败");
    } finally {
      setContribSaving(false);
    }
  };

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: "info",         label: "基本信息",   icon: "ri-user-line" },
    { key: "agreement",    label: "入会协议",   icon: "ri-pen-nib-line" },
    { key: "fee",          label: "会费记录",   icon: "ri-money-cny-circle-line" },
    { key: "activity",     label: "活动记录",   icon: "ri-calendar-check-line" },
    { key: "vote",         label: "投票记录",   icon: "ri-survey-line" },
    { key: "points",       label: "积分记录",   icon: "ri-coin-line" },
    { key: "contribution", label: "贡献记录",   icon: "ri-award-line" },
    { key: "history",      label: "操作历史",   icon: "ri-history-line" },
  ];

  const avatarColors = ["bg-red-100 text-red-600","bg-amber-100 text-amber-700","bg-teal-100 text-teal-600","bg-green-100 text-green-700","bg-pink-100 text-pink-600"];
  const avatarCls = avatarColors[h % avatarColors.length];

  return (
    <>
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">

          {/* Header */}
          <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold flex-shrink-0 ${avatarCls}`}>
                {member.realName.slice(0, 1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h2 className="text-lg font-bold text-gray-900">{member.realName}</h2>
                  <span className="text-sm text-gray-400">@{member.name}</span>
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${levelCls}`}>{member.level}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${member.status === "active" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-400"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${member.status === "active" ? "bg-green-400" : "bg-gray-300"}`}></span>
                    {member.status === "active" ? "活跃" : "未活跃"}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
                  <span className="flex items-center gap-1"><i className="ri-map-pin-line"></i>{member.city}</span>
                  <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>加入于 {member.joinDate}</span>
                  <span className="flex items-center gap-1 text-red-500 font-medium"><i className="ri-coin-line"></i>{displayPoints.toLocaleString()} 积分</span>
                  {member.memberNo && (
                    <span className="flex items-center gap-1 text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded-lg text-[11px]">
                      <i className="ri-fingerprint-2-line"></i>{member.memberNo}
                    </span>
                  )}
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer rounded-xl hover:bg-gray-100 flex-shrink-0">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`relative flex cursor-pointer items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-medium transition-all ${tab === t.key ? "bg-red-600 text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"}`}
                >
                  <i className={t.icon}></i>
                  <span className="whitespace-nowrap">{t.label}</span>
                  {t.key === "history" && levelLogs.length > 0 && (
                    <span className={`w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${tab === t.key ? "bg-white text-red-600" : "bg-red-500 text-white"}`}>
                      {levelLogs.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">

            {/* ── 基本信息 + 登记表 (merged) ── */}
            {tab === "info" && (
              <div className="space-y-5">
                {/* Member No */}
                {(memberNoLocal || member.memberNo) && (
                  <div className="bg-amber-50 rounded-2xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                        <i className="ri-fingerprint-2-line text-amber-600 text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-amber-600">会员编号</p>
                        {!memberNoEditing ? (
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <p className="text-sm font-bold text-amber-800 font-mono">{memberNoLocal || member.memberNo}</p>
                            <button
                              type="button"
                              className="text-xs text-amber-700 hover:underline"
                              onClick={() => {
                                setMemberNoDraft(memberNoLocal || member.memberNo || "");
                                setMemberNoEditing(true);
                                void (async () => {
                                  try {
                                    const [rule, reserved] = await Promise.all([
                                      fetchMemberNoRule(tenantLegacyId),
                                      fetchReservedMemberNos(tenantLegacyId),
                                    ]);
                                    const opts = (reserved || [])
                                      .filter((r) => r.isEnabled && !r.isAssigned)
                                      .map((r) => composeMemberNoFromRule(rule, member.level, r.sequenceTail));
                                    setReservedNoOptions(opts);
                                  } catch (e) {
                                    reportError(e, "加载编号规则失败");
                                  }
                                })();
                              }}
                            >
                              修改
                            </button>
                          </div>
                        ) : (
                          <div className="mt-2 space-y-2">
                            <input
                              value={memberNoDraft}
                              onChange={(e) => setMemberNoDraft(e.target.value)}
                              className="w-full border border-amber-200 rounded-xl px-3 py-2 text-sm font-mono bg-white"
                            />
                            <p className="text-[11px] text-amber-700/80">
                              只要未被占用即可（含大于下一序号），不会自动推进下一序号。
                            </p>
                            {reservedNoOptions.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {reservedNoOptions.map((no) => (
                                  <button
                                    key={no}
                                    type="button"
                                    onClick={() => setMemberNoDraft(no)}
                                    className="text-[11px] font-mono px-2 py-1 rounded-lg bg-white border border-amber-200 text-amber-800 hover:bg-amber-100"
                                  >
                                    {no}
                                  </button>
                                ))}
                              </div>
                            )}
                            <div className="flex gap-2">
                              <button
                                type="button"
                                disabled={memberNoBusy}
                                onClick={() => setMemberNoEditing(false)}
                                className="px-3 py-1.5 rounded-xl bg-white border border-gray-200 text-xs disabled:opacity-50"
                              >
                                取消
                              </button>
                              <button
                                type="button"
                                disabled={memberNoBusy}
                                onClick={() => {
                                  void (async () => {
                                    const next = memberNoDraft.trim();
                                    if (!next) return;
                                    setMemberNoBusy(true);
                                    try {
                                      const r = await changeMemberNo(tenantLegacyId, member.id, next);
                                      const saved = r.memberNo?.trim() || next;
                                      setMemberNoLocal(saved);
                                      setMemberNoEditing(false);
                                      showUserNotice("会员编号已更新。");
                                      onDataChanged?.();
                                    } catch (e) {
                                      reportError(e, "修改失败");
                                    } finally {
                                      setMemberNoBusy(false);
                                    }
                                  })();
                                }}
                                className="px-3 py-1.5 rounded-xl bg-red-600 text-white text-xs disabled:opacity-50"
                              >
                                {memberNoBusy ? "保存中…" : "保存"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* 会员分组 */}
                <div className="border border-teal-100 rounded-2xl overflow-hidden bg-teal-50/40">
                  <div className="px-4 py-3 border-b border-teal-100 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold text-teal-800 flex items-center gap-1.5">
                        <i className="ri-organization-chart"></i>会员分组
                      </p>
                      <p className="text-[11px] text-teal-700/70 mt-0.5">
                        当前：{formatMemberGroupsDisplay(groupsLocal, groupDefs) || "未分组"}
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={groupSaving || groupDefsLoading}
                      onClick={() => void saveMemberGroups()}
                      className="px-3 py-1.5 rounded-xl bg-teal-600 text-white text-xs font-medium hover:bg-teal-700 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                    >
                      {groupSaving ? "保存中…" : "保存分组"}
                    </button>
                  </div>
                  <div className="p-4 space-y-2 bg-white">
                    {groupDefsLoading ? (
                      <p className="text-xs text-gray-400">加载分组字典…</p>
                    ) : groupDefs.length === 0 ? (
                      <p className="text-xs text-gray-400">暂无可用分组，请先在「系统配置 → 会员分组字典」中配置。</p>
                    ) : (
                      groupDefs.map((g) => {
                        const on = groupDraft.some((x) => x.groupKey === g.id);
                        const draft = groupDraft.find((x) => x.groupKey === g.id);
                        const roles = enabledRolesForGroup(g);
                        return (
                          <div
                            key={g.id}
                            className={`rounded-xl border px-3 py-2.5 ${on ? "border-teal-300 bg-teal-50/60" : "border-gray-100"}`}
                          >
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={() => toggleGroupKey(g.id)}
                                className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                              />
                              <span className="text-sm font-medium text-gray-800">{g.name}</span>
                            </label>
                            {on && roles.length > 0 && (
                              <select
                                value={draft?.roleKey || ""}
                                onChange={(e) => setGroupRole(g.id, e.target.value)}
                                className="mt-2 w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-teal-400"
                              >
                                <option value="">不指定组内职位</option>
                                {roles.map((r) => (
                                  <option key={r.roleKey} value={r.roleKey}>
                                    {r.name}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: "会员等级", value: member.level, color: levelCls },
                    { label: "累计积分", value: `${displayPoints.toLocaleString()} 分`, color: "bg-amber-50 text-amber-700" },
                    { label: "参与活动", value: `${activityRows.filter((x) => x.checkedIn).length} / ${activityRows.length} 次`, color: "bg-teal-50 text-teal-700" },
                  ].map((c) => (
                    <div key={c.label} className={`rounded-2xl p-4 text-center ${c.color}`}>
                      <p className="text-2xl font-bold">{c.value}</p>
                      <p className="text-xs mt-1 opacity-75">{c.label}</p>
                    </div>
                  ))}
                </div>

                {/* Basic contact info */}
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "真实姓名", value: member.realName, icon: "ri-user-line" },
                    { label: "昵称",    value: member.name,    icon: "ri-at-line" },
                    { label: "手机号",  value: member.phone,   icon: "ri-phone-line" },
                    { label: "电子邮箱",value: member.email,   icon: "ri-mail-line" },
                    {
                      label: "微信绑定",
                      value: member.wxOpenId?.trim() ? "已绑定（小程序）" : "未绑定",
                      icon: "ri-wechat-fill",
                      extra: member.wxOpenId?.trim() || undefined,
                    },
                    { label: "所在城市",value: member.city,    icon: "ri-map-pin-line" },
                    { label: "加入日期",value: member.joinDate,icon: "ri-calendar-line" },
                  ].map((item) => (
                    <div key={item.label} className="bg-gray-50 rounded-2xl p-4 flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center bg-white rounded-xl flex-shrink-0">
                        <i
                          className={`${item.icon} text-sm ${item.label === "微信绑定" ? "" : "text-gray-400"}`}
                          style={item.label === "微信绑定" ? { color: "#07C160" } : undefined}
                        ></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-400">{item.label}</p>
                        <p className="text-sm font-semibold text-gray-800 mt-0.5">{item.value}</p>
                        {"extra" in item && item.extra ? (
                          <p className="text-[11px] font-mono text-gray-600 mt-1 break-all leading-snug">{item.extra}</p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-100"></div>
                  <span className="text-xs text-gray-400 flex items-center gap-1.5 whitespace-nowrap">
                    <i className="ri-file-list-3-line text-red-500"></i>入会申请登记信息
                  </span>
                  <div className="flex-1 h-px bg-gray-100"></div>
                </div>

                {enrollmentLoading ? (
                  <div className="text-center py-12 text-sm text-gray-400">加载登记信息…</div>
                ) : enrollment && enrollment.found ? (
                  <>
                    <div className="border border-gray-200 rounded-2xl overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                        <p className="text-xs font-bold text-gray-600 flex items-center gap-2">
                          <i className="ri-user-3-line text-red-500"></i>个人基本信息
                        </p>
                      </div>
                      {[
                        { label: "性别", value: enrollment.gender || "—" },
                        { label: "民族", value: enrollment.ethnicity || "—" },
                        { label: "政治面貌", value: enrollment.politicalStatus || "—" },
                        { label: "血型", value: enrollment.bloodType || "—" },
                        { label: "籍贯", value: enrollment.hometown || member.city },
                        { label: "服装尺码", value: enrollment.clothingSize || "—" },
                      ].map((row) => (
                        <div key={row.label} className="flex border-b border-gray-100 last:border-b-0">
                          <div className="w-28 flex-shrink-0 bg-gray-50 px-4 py-2.5 border-r border-gray-100">
                            <span className="text-xs font-medium text-gray-500">{row.label}</span>
                          </div>
                          <div className="px-4 py-2.5 flex-1">
                            <span className="text-xs text-gray-800">{row.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border border-gray-200 rounded-2xl overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                        <p className="text-xs font-bold text-gray-600 flex items-center gap-2">
                          <i className="ri-id-card-line text-red-500"></i>证件与联系信息
                        </p>
                      </div>
                      {[
                        { label: "证件类型", value: enrollment.idType || "—" },
                        { label: "证件号码", value: enrollment.idNumber || "—" },
                        { label: "工作单位", value: enrollment.organization || "—" },
                        { label: "紧急联系人", value: enrollment.emergencyContact || "—" },
                        { label: "紧急联系人电话", value: enrollment.emergencyContactPhone || "—" },
                      ].map((row) => (
                        <div key={row.label} className="flex border-b border-gray-100 last:border-b-0">
                          <div className="w-28 flex-shrink-0 bg-gray-50 px-4 py-2.5 border-r border-gray-100">
                            <span className="text-xs font-medium text-gray-500">{row.label}</span>
                          </div>
                          <div className="px-4 py-2.5 flex-1">
                            <span className="text-xs text-gray-800">{row.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border border-gray-200 rounded-2xl overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                        <p className="text-xs font-bold text-gray-600 flex items-center gap-2">
                          <i className="ri-chat-heart-line text-red-500"></i>申请理由
                        </p>
                      </div>
                      <div className="px-4 py-3">
                        <p className="text-xs text-gray-700 leading-relaxed">{enrollment.reason || "—"}</p>
                      </div>
                    </div>

                    {enrollment.resumes.length > 0 && (
                      <div className="border border-gray-200 rounded-2xl overflow-hidden">
                        <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                          <p className="text-xs font-bold text-gray-600 flex items-center gap-2">
                            <i className="ri-briefcase-line text-red-500"></i>简历经历
                          </p>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {enrollment.resumes.map((r, idx) => (
                            <div key={idx} className="px-4 py-3 text-xs text-gray-700">
                              <p className="font-medium text-gray-800">{r.organization} · {r.position}</p>
                              <p className="text-gray-400 mt-0.5">{r.startDate} — {r.endDate}{r.area ? ` · ${r.area}` : ""}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-10 text-sm text-gray-400 border border-dashed border-gray-200 rounded-2xl">
                    暂无已审核通过的入会申请登记信息（或与当前会员手机号不一致）
                  </div>
                )}

                <div className="bg-amber-50 rounded-xl px-4 py-3 flex items-start gap-2">
                  <i className="ri-lock-line text-amber-500 flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    部分敏感信息（身份证号、详细地址）已进行脱敏处理，完整信息请联系系统管理员查阅原始档案。
                  </p>
                </div>
              </div>
            )}

            {/* ── 入会协议 ── */}
            {tab === "agreement" && (
              <div className="space-y-4">
                {agreementTabBusy ? (
                  <div className="text-center py-12 text-sm text-gray-400 flex flex-col items-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-2xl text-red-400"></i>
                    正在加载协议信息…
                  </div>
                ) : (
                  <>
                    <div className="bg-white border-2 border-gray-100 rounded-2xl p-5">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex gap-3 min-w-0">
                          <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center flex-shrink-0">
                            <i className="ri-file-text-line text-red-600 text-xl"></i>
                          </div>
                          <div className="min-w-0">
                            <h4 className="font-bold text-gray-800 text-sm">
                              《{agreementCardDoc?.title?.trim() || `${agreementOrgLabel} 入会协议`}》
                            </h4>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                              {enrollmentSnapshotDoc ? (
                                <span className="text-green-700 font-medium">申请时存档</span>
                              ) : null}
                              {agreementCardDoc?.version ? (
                                <span>版本 {agreementCardDoc.version}</span>
                              ) : null}
                              {agreementCardDoc?.updatedAt ? (
                                <span>更新 {agreementCardDoc.updatedAt}</span>
                              ) : null}
                              {agreementCardDoc?.requireSign ? (
                                <span className="text-amber-600 font-medium">需签署</span>
                              ) : (
                                <span className="text-gray-400">未要求签署</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                              {enrollmentSnapshotDoc
                                ? "正文与标题来自该会员已通过审核的入会申请存档（提交当时展示的协议）。"
                                : "标题与正文来自协会后台「系统设置 → 协议与文书」当前配置（申请时未存档正文或历史数据）。"}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowAgreementPreview(true)}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white text-sm rounded-xl cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap flex-shrink-0 self-start sm:self-center"
                        >
                          <i className="ri-eye-line"></i>
                          预览全文
                        </button>
                      </div>
                    </div>

                    {enrollmentLoading ? (
                      <div className="text-center py-8 text-sm text-gray-400">加载入会申请存档…</div>
                    ) : enrollment && enrollment.found ? (
                      <>
                        <div className="flex items-center gap-2 bg-green-50 rounded-xl px-4 py-3">
                          <i className="ri-shield-check-line text-green-600 flex-shrink-0"></i>
                          <div>
                            <p className="text-sm font-bold text-green-800">入会协议签署状态</p>
                            <p className="text-xs text-green-600">
                              {enrollment.hasSigned
                                ? `申请已通过审核，登记签署信息来自入会申请（申请日 ${enrollment.applyDate}）`
                                : "申请已通过审核，签署状态以档案为准"}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { label: "签署人", value: member.realName },
                            { label: "申请审核通过日", value: enrollment.applyDate },
                            { label: "协会名称", value: assocDisplayName },
                            {
                              label: "签署状态",
                              value: enrollment.hasSigned ? "已提交签署/凭证" : "未标记签署",
                              isGreen: enrollment.hasSigned,
                            },
                          ].map((row) => (
                            <div key={row.label} className="bg-gray-50 rounded-xl p-3">
                              <p className="text-xs text-gray-400 mb-0.5">{row.label}</p>
                              <p
                                className={`font-medium text-sm ${"isGreen" in row && row.isGreen ? "text-green-600 flex items-center gap-1" : "text-gray-900"}`}
                              >
                                {"isGreen" in row && row.isGreen && <i className="ri-check-circle-line"></i>}
                                {row.value}
                              </p>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-500 leading-relaxed">
                        <p className="font-medium text-gray-600 mb-1">暂无入会申请存档</p>
                        未匹配到与该会员手机号一致的、已审核通过的入会申请（可能为历史导入会员或信息不一致）。上方协议正文仍以当前后台配置为准，可点击「预览全文」查看。
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── 会费记录 ── */}
            {tab === "fee" && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-700">会费缴纳记录</h4>
                  <span className="text-xs text-gray-400">{feeLoading ? "加载中…" : `共 ${feeRecords.length} 条`}</span>
                </div>
                {feeLoading ? (
                  <div className="text-center py-12 text-sm text-gray-400">加载中…</div>
                ) : feeRecords.length === 0 ? (
                  <div className="text-center py-12 text-sm text-gray-400">暂无会费与续费申请记录</div>
                ) : (
                <div className="space-y-2">
                  {feeRecords.map((r, i) => (
                    <div key={r.id + i} className="flex items-center gap-4 bg-gray-50 rounded-2xl px-4 py-3">
                      <div className="w-9 h-9 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-money-cny-circle-line text-green-600 text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">{r.type}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{r.date} · {r.method}</p>
                      </div>
                      <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                        <p className={`text-sm font-bold ${r.amount > 0 ? "text-gray-800" : "text-gray-400"}`}>
                          {r.amount > 0 ? `¥${r.amount}` : "免费"}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full">{r.status}</span>
                          <button
                            onClick={() => setVoucherRecord(r)}
                            className="text-xs bg-amber-50 text-amber-600 hover:bg-amber-100 px-2 py-0.5 rounded-full cursor-pointer flex items-center gap-1 whitespace-nowrap"
                          >
                            <i className="ri-receipt-line text-xs"></i>查看凭证
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}

            {/* ── 活动记录 ── */}
            {tab === "activity" && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-700">参与活动记录</h4>
                  <span className="text-xs text-gray-400">{activityLoading ? "加载中…" : `共 ${activityRows.length} 条`}</span>
                </div>
                {activityLoading ? (
                  <div className="text-center py-12 text-sm text-gray-400">加载中…</div>
                ) : activityRows.length === 0 ? (
                  <div className="text-center py-12 text-sm text-gray-400">暂无活动报名记录</div>
                ) : (
                <div className="space-y-2">
                  {activityRows.map((a, i) => {
                    const ui = activityParticipationUi(a);
                    return (
                    <div key={a.activityId + i} className="flex items-center gap-4 bg-gray-50 rounded-2xl px-4 py-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${typeColor[a.activityType] ?? "bg-gray-100 text-gray-500"}`}>
                        <i className="ri-calendar-event-line text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{a.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-400">活动 {a.eventDate} · 报名 {a.applyDate}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${typeColor[a.activityType] ?? "bg-gray-100 text-gray-500"}`}>{a.activityType}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {a.checkedIn && <span className="text-xs bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full flex items-center gap-1"><i className="ri-check-line"></i>已签到</span>}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${ui.rowClass}`}>{ui.status}</span>
                      </div>
                    </div>
                    );
                  })}
                </div>
                )}
              </div>
            )}

            {/* ── 投票记录 ── */}
            {tab === "vote" && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-700">投票参与记录</h4>
                  <span className="text-xs text-gray-400">共 {voteRecords.length} 条</span>
                </div>
                {voteRecords.length === 0 ? (
                  <div className="text-center py-12 text-sm text-gray-400">暂无投票参与记录（与当前后台同步的投票数据）</div>
                ) : (
                <div className="space-y-2">
                  {voteRecords.map((v, i) => (
                    <div key={v.title + i} className="bg-gray-50 rounded-2xl px-4 py-3">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <p className="text-sm font-medium text-gray-800">{v.title}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${v.status === "进行中" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>{v.status}</span>
                      </div>
                      <p className="text-xs text-gray-400 mb-1.5">{v.time}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {v.voted.split("、").filter(Boolean).map((opt) => (
                          <span key={opt} className="text-xs bg-red-50 text-red-600 px-2.5 py-1 rounded-lg border border-red-100 flex items-center gap-1">
                            <i className="ri-check-line text-xs"></i>{opt}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}

            {/* ── 积分记录 ── */}
            {tab === "points" && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-700">积分流水记录</h4>
                  <span className="text-xs bg-red-50 text-red-600 px-2.5 py-1 rounded-full font-semibold">
                    {pointsLoading ? "加载中…" : `当前 ${displayPoints.toLocaleString()} 分`}
                  </span>
                </div>
                {pointsLoading ? (
                  <div className="text-center py-12 text-sm text-gray-400">加载中…</div>
                ) : pointRows.length === 0 ? (
                  <div className="text-center py-12 text-sm text-gray-400">暂无积分流水</div>
                ) : (
                <div className="space-y-2">
                  {pointRows.map((r) => (
                    <div key={r.id} className="flex items-center gap-4 bg-gray-50 rounded-2xl px-4 py-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${r.delta > 0 ? "bg-green-100 text-green-600" : "bg-red-100 text-red-500"}`}>
                        <i className={r.delta > 0 ? "ri-add-line" : "ri-subtract-line"}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">{r.memo}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{r.date} · {categoryLabelZh(r.category)}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-sm font-bold ${r.delta > 0 ? "text-green-600" : "text-red-500"}`}>
                          {r.delta > 0 ? `+${r.delta}` : r.delta}
                        </p>
                        <p className="text-xs text-gray-400">余 {Math.max(0, r.balanceAfter)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}

            {/* ── 贡献记录 ── */}
            {tab === "contribution" && (
              <div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { label: "贡献流水", value: `${contributionLedger.length} 条`, color: "bg-teal-50 text-teal-700" },
                    { label: "正向积分", value: `${totalContribCredits} 分`, color: "bg-amber-50 text-amber-700" },
                    { label: "最近余额", value: `${displayPoints.toLocaleString()} 分`, color: "bg-green-50 text-green-700" },
                  ].map((c) => (
                    <div key={c.label} className={`rounded-2xl p-3 text-center ${c.color}`}>
                      <p className="text-xl font-bold">{c.value}</p>
                      <p className="text-xs mt-0.5 opacity-75">{c.label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">贡献明细</h4>
                  <button onClick={() => { setShowAddContrib(true); setAddForm(emptyAddForm()); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white text-xs font-medium rounded-xl hover:bg-teal-700 cursor-pointer transition-colors whitespace-nowrap">
                    <i className="ri-add-line"></i>新增贡献记录
                  </button>
                </div>
                {showAddContrib && (
                  <div className="bg-teal-50/60 border border-teal-200 rounded-2xl p-4 mb-4 space-y-3">
                    <p className="text-xs font-semibold text-teal-700 flex items-center gap-1.5 mb-1"><i className="ri-award-line"></i>新增贡献记录</p>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">贡献类别 <span className="text-red-500">*</span></label>
                      {activeCategories.length === 0 ? (
                        <p className="text-xs text-gray-400 bg-white rounded-xl px-3 py-2 border border-gray-200">暂无启用的贡献类别</p>
                      ) : (
                        <select value={addForm.categoryId} onChange={(e) => setAddForm((p) => ({ ...p, categoryId: e.target.value, quantity: "" }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 bg-white cursor-pointer">
                          <option value="">— 请选择 —</option>
                          {activeCategories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}（{cat.unit} / {cat.pointsPerUnit} 积分）</option>))}
                        </select>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">数量（{selectedCat?.unit ?? "单位"}）<span className="text-red-500">*</span></label>
                        <input type="number" value={addForm.quantity} onChange={(e) => setAddForm((p) => ({ ...p, quantity: e.target.value === "" ? "" : Number(e.target.value) }))} placeholder="如：4" min={1} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 bg-white" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">自动计算积分</label>
                        <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm ${autoPoints > 0 ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-gray-50 border-gray-200 text-gray-400"}`}>
                          <i className="ri-coin-line text-sm"></i>
                          <span className="font-semibold">{autoPoints > 0 ? `+${autoPoints} 积分` : "—"}</span>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">贡献日期 <span className="text-red-500">*</span></label>
                        <input type="date" value={addForm.date} onChange={(e) => setAddForm((p) => ({ ...p, date: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 bg-white cursor-pointer" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">角色/说明</label>
                        <input type="text" value={addForm.role} onChange={(e) => setAddForm((p) => ({ ...p, role: e.target.value }))} placeholder="如：现场引导志愿者" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-teal-400 bg-white" />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button type="button" onClick={() => setShowAddContrib(false)} className="flex-1 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
                      <button type="button" onClick={() => void handleAddContrib()} disabled={!canSubmitContrib || contribSaving} className={`flex-1 py-2 text-sm rounded-xl cursor-pointer whitespace-nowrap transition-colors ${canSubmitContrib && !contribSaving ? "bg-teal-600 text-white hover:bg-teal-700" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}>{contribSaving ? "提交中…" : "确认新增"}</button>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  {contributionLedger.length === 0 ? (
                    <div className="text-center py-10 text-gray-400">
                      <i className="ri-award-line text-3xl block mb-2 text-gray-200"></i>
                      <p className="text-sm">暂无贡献类积分记录</p>
                      <p className="text-xs text-gray-300 mt-1">通过「新增贡献记录」将写入服务端积分流水（类别：贡献）</p>
                    </div>
                  ) : contributionLedger.map((c) => (
                    <div key={c.id} className="flex items-center gap-4 bg-gray-50 rounded-2xl px-4 py-3">
                      <div className="w-9 h-9 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-award-line text-teal-600 text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{c.memo}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{c.date} · {c.createdAt}</p>
                      </div>
                      <div className="text-right flex-shrink-0 space-y-1">
                        <p className={`text-xs font-semibold ${c.delta >= 0 ? "text-amber-600" : "text-red-500"}`}>
                          {c.delta > 0 ? `+${c.delta}` : c.delta} 分
                        </p>
                        <span className="text-xs px-2 py-0.5 rounded-full block text-center bg-green-50 text-green-600">已入账</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── 操作历史 ── */}
            {tab === "history" && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-700">等级变更操作历史</h4>
                  <span className="text-xs text-gray-400">
                    {levelLogsLoading ? "加载中…" : `共 ${levelLogs.length} 条`}
                  </span>
                </div>
                {levelLogsLoading ? (
                  <div className="text-center py-16 text-sm text-gray-400">加载中…</div>
                ) : levelLogs.length === 0 ? (
                  <div className="text-center py-16">
                    <i className="ri-history-line text-4xl text-gray-200 block mb-3"></i>
                    <p className="text-sm text-gray-400">暂无等级变更记录</p>
                    <p className="text-xs text-gray-300 mt-1">在会员列表中「修改等级」后，记录将同步保存并在此展示</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {levelLogs.map((log, idx) => (
                      <div key={log.id} className="relative flex gap-4">
                        {/* Timeline line */}
                        {idx < levelLogs.length - 1 && (
                          <div className="absolute left-4 top-9 bottom-0 w-px bg-gray-200" style={{ height: "calc(100% + 12px)" }}></div>
                        )}
                        <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-full flex-shrink-0 z-10 border-2 border-amber-200">
                          <i className="ri-medal-line text-amber-600 text-sm"></i>
                        </div>
                        <div className="flex-1 bg-gray-50 rounded-2xl px-4 py-3 pb-4">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-gray-800">等级调整</span>
                              <span className="flex items-center gap-1.5">
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${memberLevelStore.getClassName(log.oldLevel)}`}>{log.oldLevel}</span>
                                <i className="ri-arrow-right-line text-gray-400 text-xs"></i>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${memberLevelStore.getClassName(log.newLevel)}`}>{log.newLevel}</span>
                              </span>
                            </div>
                            <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{log.date} {log.time}</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <i className="ri-user-settings-line"></i>操作人：{log.operator}
                            </span>
                            {log.note && (
                              <span className="flex items-center gap-1">
                                <i className="ri-file-text-line"></i>原因：{log.note}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6 bg-amber-50 rounded-xl px-4 py-3 flex items-start gap-2">
                  <i className="ri-information-line text-amber-500 flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    以上记录由服务端保存，与会员当前等级数据一致。
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showAgreementPreview && (
        <AgreementEnrollmentPreviewModal
          tenantLegacyId={tenantLegacyId}
          memberName={member.realName}
          memberNo={member.memberNo}
          idNumber={enrollment && enrollment.found ? enrollment.idNumber : undefined}
          phone={member.phone}
          signDate={enrollment && enrollment.found ? enrollment.applyDate : member.joinDate}
          orgLabel={agreementOrgLabel}
          sealLine1={sealLine1}
          sealLine2={sealLine2}
          variant="member"
          enrollmentMatched={Boolean(enrollment && enrollment.found)}
          signatureImageUrl={
            enrollment && enrollment.found
              ? enrollment.agreementSignatureDataUrl?.trim() || null
              : null
          }
          agreementSnapshot={
            enrollment && enrollment.found && enrollment.agreementSnapshotContent?.trim()
              ? {
                  title: enrollment.agreementSnapshotTitle,
                  content: enrollment.agreementSnapshotContent,
                  version: enrollment.agreementSnapshotVersion,
                }
              : null
          }
          onClose={() => setShowAgreementPreview(false)}
        />
      )}

      {voucherRecord && (
        <VoucherModal
          record={voucherRecord}
          payeeName={assocDisplayName}
          collectModeOnline={collectModeOnline}
          onClose={() => setVoucherRecord(null)}
        />
      )}
    </>
  );
}
